import { useRef, useState } from 'react';
import { Hourglass, Plus, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { webinarAiGenerationService } from '@/services/webinarAiGeneration.service';
import { clearCurrentWebinar } from '@/features/webinar/webinarSlice';
import type { AppError } from '@/features/auth/types';

const TOTAL_STEPS = 7;

/**
 * Toutes les réponses du wizard vivent ici, dans le composant parent qui reste monté
 * pendant toute la traversée des étapes (pas de route par étape) : revenir en arrière
 * ne fait donc jamais perdre ce qui a été saisi.
 */
type AiWizardFormData = {
  pitch: string;
  idealClient: string;
  falseBeliefs: string;
  uniqueMethod: string;
  callToAction: string;
  offerLink: string;
  slidesFile: File | null;
};

const INITIAL_FORM_DATA: AiWizardFormData = {
  pitch: '',
  idealClient: '',
  falseBeliefs: '',
  uniqueMethod: '',
  callToAction: '',
  offerLink: '',
  slidesFile: null,
};

const QUESTION_MAX_LENGTH = 10000;
const OFFER_LINK_MAX_LENGTH = 1024;

type ValidationKey =
  | 'required'
  | 'blank'
  | 'tooLong'
  | 'offerLinkTooLong'
  | 'offerLinkInvalid'
  | 'slideInvalid'
  | 'slideDuplicate'
  | 'slideTypeUnsupported'
  | 'slideNotReady'
  | 'slideTotalSizeExceeded'
  | 'slideTooLarge';

function isValidHttpUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateQuestion(value: string): ValidationKey | null {
  if (value.length === 0) return 'required';
  if (value.trim().length === 0) return 'blank';
  if (value.length > QUESTION_MAX_LENGTH) return 'tooLong';
  return null;
}

function validateOfferLink(value: string): ValidationKey | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > OFFER_LINK_MAX_LENGTH) return 'offerLinkTooLong';
  if (!isValidHttpUrl(trimmed)) return 'offerLinkInvalid';
  return null;
}

function getStepError(step: number, formData: AiWizardFormData): ValidationKey | null {
  if (step === 1) return validateQuestion(formData.pitch);
  if (step === 2) return validateQuestion(formData.idealClient);
  if (step === 3) return validateQuestion(formData.falseBeliefs);
  if (step === 4) return validateQuestion(formData.uniqueMethod);
  if (step === 5) return validateQuestion(formData.callToAction);
  if (step === 6) return validateOfferLink(formData.offerLink);
  return null;
}

const FIELD_TO_STEP: Record<string, number> = {
  questionOne: 1,
  questionTwo: 2,
  questionThree: 3,
  questionFour: 4,
  questionFive: 5,
  offerLink: 6,
  slideResourceIds: 7,
  fileName: 7,
  contentType: 7,
  fileSize: 7,
};

function mapBackendValidationCode(code: string): ValidationKey | null {
  if (code.includes('OFFER_LINK.TOO_LONG')) return 'offerLinkTooLong';
  if (code.includes('OFFER_LINK.INVALID')) return 'offerLinkInvalid';
  if (code.includes('SLIDES.CONTAINS_NULL')) return 'slideInvalid';
  if (code.includes('SLIDES.DUPLICATES')) return 'slideDuplicate';
  if (code.includes('SLIDE.TYPE_UNSUPPORTED')) return 'slideTypeUnsupported';
  if (code.includes('SLIDE.NOT_READY')) return 'slideNotReady';
  if (code.includes('SLIDES.TOTAL_SIZE_EXCEEDED')) return 'slideTotalSizeExceeded';
  if (code.includes('RESOURCE.FILE.TOO_LARGE') || code.includes('RESOURCE.FILE.TOO.LARGE')) {
    return 'slideTooLarge';
  }
  if (
    code.includes('RESOURCE.FILENAME') ||
    code.includes('RESOURCE.CONTENT_TYPE') ||
    code.includes('RESOURCE.FILE.SIZE_MIN_1')
  ) {
    return 'slideInvalid';
  }
  if (code.endsWith('.REQUIRED')) return 'required';
  if (code.endsWith('.BLANK')) return 'blank';
  if (code.endsWith('.TOO_LONG')) return 'tooLong';
  return null;
}

const STEP_IMAGES: Record<number, string> = {
  1: '/assets/ai-wizard.jpg',
  2: '/assets/ai-wizard-step2.jpg',
  3: '/assets/ai-wizard-step3.jpg',
  4: '/assets/ai-wizard-step4.jpg',
  5: '/assets/ai-wizard-step5.jpg',
};

// Long polling du statut de génération : intervalle entre deux appels et durée max avant abandon.
const GENERATION_POLL_INTERVAL_MS = 2000;
const GENERATION_POLL_MAX_DURATION_MS = 5 * 60 * 1000;

const wait = (durationMs: number) => new Promise((resolve) => setTimeout(resolve, durationMs));

/** Position, taille et décalage d'animation fixes pour que chaque particule bouge indépendamment sans re-render aléatoire. */
const PARTICLES = [
  { top: '0%', left: '8%', size: 8, duration: 6.5, delay: 0, blur: 1 },
  { top: '10%', left: '88%', size: 6, duration: 7.2, delay: 1.1 },
  { top: '88%', left: '3%', size: 10, duration: 8, delay: 0.4, blur: 1.5 },
  { top: '96%', left: '80%', size: 7, duration: 6, delay: 2 },
  { top: '45%', left: '-10%', size: 5, duration: 9, delay: 1.6, blur: 1 },
  { top: '38%', left: '105%', size: 9, duration: 7.8, delay: 0.8 },
  { top: '-8%', left: '50%', size: 6, duration: 6.8, delay: 2.4, blur: 1.5 },
  { top: '105%', left: '42%', size: 8, duration: 8.4, delay: 1.3 },
  { top: '2%', left: '50%', size: 16, duration: 7, delay: 0.5, blur: 4, color: '#2EFFB8' },
  { top: '88%', left: '50%', size: 16, duration: 7.5, delay: 1.2, blur: 4, color: '#7C6BFF' },
];

export default function AiWizardSection({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('webinars');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = workspace?.id;
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<AiWizardFormData>(INITIAL_FORM_DATA);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  // N'affiche l'erreur d'un champ qu'une fois qu'on y a touché (saisie ou tentative de "Suivant") :
  // sinon un champ vide affiche "Ce champ est obligatoire." dès l'arrivée sur l'étape.
  const [touchedSteps, setTouchedSteps] = useState<Set<number>>(new Set());
  // Erreur remontée par le back à la soumission finale (rattachée à une étape précise).
  const [serverFieldError, setServerFieldError] = useState<{
    step: number;
    key: ValidationKey;
  } | null>(null);
  const [slideError, setSlideError] = useState<ValidationKey | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const touchStep = (targetStep: number) =>
    setTouchedSteps((prev) => (prev.has(targetStep) ? prev : new Set(prev).add(targetStep)));

  const handleNext = () => {
    touchStep(step);
    if (getStepError(step, formData) != null) return;
    setServerFieldError(null);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const handleGenerate = async () => {
    if (workspaceId == null) return;

    setGenerationError(null);
    setIsGenerating(true);
    try {
      let slideResourceIds: number[] | undefined;
      if (formData.slidesFile) {
        const file = formData.slidesFile;
        const uploadInfo = await webinarAiGenerationService.generateSlidesUploadUrl(workspaceId, {
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        });
        await webinarAiGenerationService.uploadSlidesFile(uploadInfo.uploadUrl, file);
        await webinarAiGenerationService.markResourceReady(workspaceId, uploadInfo.resourceId);
        slideResourceIds = [uploadInfo.resourceId];
      }

      const trimmedOfferLink = formData.offerLink.trim();

      const { generationId } = await webinarAiGenerationService.createGeneration(workspaceId, {
        questionOne: formData.pitch,
        questionTwo: formData.idealClient,
        questionThree: formData.falseBeliefs,
        questionFour: formData.uniqueMethod,
        questionFive: formData.callToAction,
        offerLink: trimmedOfferLink.length > 0 ? trimmedOfferLink : undefined,
        slideResourceIds,
      });

      const deadline = Date.now() + GENERATION_POLL_MAX_DURATION_MS;
      let result = await webinarAiGenerationService.getGenerationStatus(workspaceId, generationId);
      while (result.status === 'PENDING' || result.status === 'PROCESSING') {
        if (Date.now() >= deadline) {
          throw new Error('Webinar AI generation timed out');
        }
        await wait(GENERATION_POLL_INTERVAL_MS);
        result = await webinarAiGenerationService.getGenerationStatus(workspaceId, generationId);
      }

      if (result.status === 'FAILED' || result.webinarId == null) {
        throw new Error(result.error ?? 'Webinar AI generation failed');
      }

      dispatch(clearCurrentWebinar());
      navigate(`/create-webinar/configuration/${result.webinarId}`);
      onClose();
    } catch (error) {
      const appError = error as AppError | undefined;
      const details = Array.isArray(appError?.details)
        ? (appError.details as Array<{ message?: string; fieldName?: string }>)
        : [];
      const fieldDetail = details.find(
        (detail) => detail.fieldName && FIELD_TO_STEP[detail.fieldName] != null,
      );

      const applyFieldError = (targetStep: number, key: ValidationKey) => {
        setStep(targetStep);
        touchStep(targetStep);
        if (targetStep === 7) {
          setSlideError(key);
        } else {
          setServerFieldError({ step: targetStep, key });
        }
      };

      let handledAsFieldError = false;

      if (fieldDetail?.fieldName && fieldDetail.message) {
        const targetStep = FIELD_TO_STEP[fieldDetail.fieldName];
        const key = mapBackendValidationCode(fieldDetail.message);
        if (key) {
          applyFieldError(targetStep, key);
          handledAsFieldError = true;
        }
      } else if (appError?.message?.includes('WEBINAR_AI.SLIDE')) {
        const key = mapBackendValidationCode(appError.message);
        if (key) {
          applyFieldError(7, key);
          handledAsFieldError = true;
        }
      }
      if (!handledAsFieldError) {
        setGenerationError(t('createAndList.aiWizard.generating.error'));
      }

      toast.error(t('createAndList.aiWizard.generating.error'));
      setIsGenerating(false);
    }
  };

  const handleFileSelected = (file: File | null | undefined) => {
    if (!file) return;

    const current = formData.slidesFile;
    if (current && current.name === file.name && current.size === file.size) {
      setSlideError('slideDuplicate');
      return;
    }

    setSlideError(null);
    setFormData((prev) => ({ ...prev, slidesFile: file }));
  };

  const handleRemoveFile = () => {
    setSlideError(null);
    setFormData((prev) => ({ ...prev, slidesFile: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const currentStepError = getStepError(step, formData);
  const displayedStepError =
    serverFieldError?.step === step
      ? serverFieldError.key
      : touchedSteps.has(step)
        ? currentStepError
        : null;
  const canGoNext = currentStepError == null;
  const isLastStep = step === TOTAL_STEPS;
  const stepImage = STEP_IMAGES[step];

  const renderCounterRow = (value: string, maxLength: number, error: ValidationKey | null) => (
    <div className="flex items-center justify-between gap-2">
      {error ? (
        <p className="text-xs text-error">{t(`createAndList.aiWizard.validation.${error}`)}</p>
      ) : (
        <span />
      )}
      <p
        className={cn(
          'text-xs shrink-0',
          value.length > maxLength ? 'text-error' : 'text-white/50',
        )}
      >
        {value.length}/{maxLength}
      </p>
    </div>
  );

  if (isGenerating) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <style>{`
          @keyframes hourglass-spin {
            0%, 20% { transform: rotate(0deg); }
            50%, 70% { transform: rotate(180deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes particle-float {
            0% { transform: translate(0, 0); }
            25% { transform: translate(6px, -10px); }
            50% { transform: translate(-4px, -18px); }
            75% { transform: translate(-8px, -6px); }
            100% { transform: translate(0, 0); }
          }
          @keyframes particle-fade-in {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes particle-color {
            0% { background-color: #2EFFB8; }
            33% { background-color: #7C6BFF; }
            66% { background-color: #2ED0FF; }
            100% { background-color: #2EFFB8; }
          }
          @keyframes buoy-float {
            0% { transform: translate(0, 0) scale(1) rotate(0deg); border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%; }
            20% { transform: translate(3px, -4px) scale(1.03) rotate(4deg); border-radius: 48% 52% 54% 46% / 52% 47% 53% 48%; }
            40% { transform: translate(-4px, 3px) scale(0.97) rotate(-3deg); border-radius: 54% 46% 47% 53% / 46% 54% 46% 54%; }
            60% { transform: translate(3px, 4px) scale(1.02) rotate(3deg); border-radius: 46% 54% 51% 49% / 54% 48% 52% 46%; }
            80% { transform: translate(-3px, -2px) scale(0.98) rotate(-2deg); border-radius: 52% 48% 46% 54% / 48% 52% 47% 53%; }
            100% { transform: translate(0, 0) scale(1) rotate(0deg); border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%; }
          }
          @keyframes buoy-float-alt {
            0% { transform: translate(0, 0) scale(1) rotate(0deg); border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%; }
            25% { transform: translate(-4px, 3px) scale(1.04) rotate(-5deg); border-radius: 55% 45% 42% 58% / 45% 58% 42% 55%; }
            50% { transform: translate(4px, -3px) scale(0.96) rotate(4deg); border-radius: 42% 58% 55% 45% / 58% 42% 55% 45%; }
            75% { transform: translate(-3px, -4px) scale(1.02) rotate(-3deg); border-radius: 58% 42% 45% 55% / 42% 55% 45% 58%; }
            100% { transform: translate(0, 0) scale(1) rotate(0deg); border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%; }
          }
        `}</style>

        <div className="w-full max-w-4xl rounded-[20px] bg-dark text-white p-6 lg:p-12 flex flex-col items-center gap-8 text-center">
          <div className="gap-2">
            <p className="text-sm text-white">{t('createAndList.aiWizard.badge')}</p>
            <h1 className="text-3xl font-semibold">
              {t('createAndList.aiWizard.generating.title')}
            </h1>
          </div>

          <div className="relative flex items-center justify-center size-40">
            {PARTICLES.map((particle, index) => (
              <span
                key={index}
                className="absolute rounded-full"
                style={{
                  top: particle.top,
                  left: particle.left,
                  width: particle.size,
                  height: particle.size,
                  backgroundColor: particle.color,
                  filter: particle.blur ? `blur(${particle.blur}px)` : undefined,
                  animation: `particle-fade-in 0.8s ease-out ${index * 0.08}s both, particle-float ${particle.duration}s ease-in-out ${particle.delay}s infinite${particle.color ? '' : `, particle-color ${particle.duration * 1.5}s ease-in-out ${particle.delay}s infinite`}`,
                }}
              />
            ))}

            <div
              className="absolute inset-7 blur-[1.5px]"
              style={{
                background:
                  'radial-gradient(circle at 65% 70%, #7C6BFF 0%, #2ED0FF 45%, #2EFFB8 80%, #0C0E21 100%)',
                animation: 'buoy-float-alt 16s ease-in-out infinite',
              }}
            />
            <div
              className="absolute inset-8 blur-[1.5px]"
              style={{
                background:
                  'radial-gradient(circle at 32% 28%, #2EFFB8 0%, #2ED0FF 40%, #7C6BFF 75%, #0C0E21 100%)',
                boxShadow:
                  'inset -10px -10px 22px rgba(0,0,0,0.55), inset 8px 8px 18px rgba(255,255,255,0.4), 0 0 36px rgba(124,107,255,0.9)',
                animation: 'buoy-float 15s ease-in-out infinite',
              }}
            />

            <div
              className="absolute rounded-full"
              style={{
                width: 92.802,
                height: 92.802,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#0C0E21',
                opacity: 0.7,
                filter: 'blur(2.6727113723754883px)',
              }}
            />
            <div
              className="relative flex items-center justify-center size-16 rounded-full bg-dark  shadow"
              style={{
                width: 75.727,
                height: 75.727,

                backgroundColor: '#0C0E21',
                opacity: 0.8,
                boxShadow: '1.485px 0 6.459px 0 rgba(255, 255, 255, 0.11) inset',
                filter: 'blur(0.37120991945266724px)',
              }}
            >
              <svg width="0" height="0" className="absolute">
                <defs>
                  <linearGradient
                    id="hourglass-gradient"
                    gradientUnits="userSpaceOnUse"
                    x1="0"
                    y1="0"
                    x2="24"
                    y2="24"
                  >
                    <stop offset="0%" stopColor="#2EFFB8" />
                    <stop offset="50%" stopColor="#2ED0FF" />
                    <stop offset="100%" stopColor="#7C6BFF" />
                  </linearGradient>
                </defs>
              </svg>
              <Hourglass
                className="size-8"
                color="url(#hourglass-gradient)"
                style={{ animation: 'hourglass-spin 4s ease-in-out infinite' }}
              />
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-white/80">{t('createAndList.aiWizard.generating.wait')}</p>
            <p className="text-sm text-white/50">{t('createAndList.aiWizard.generating.detail')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-4xl rounded-[20px] bg-dark text-white p-8 flex gap-12">
        {stepImage && (
          <img
            src={stepImage}
            alt=""
            className="hidden sm:block w-2/5 h-auto rounded-2xl object-cover"
          />
        )}

        <div className={cn('w-full flex flex-col gap-6', stepImage && 'sm:w-3/5')}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <p className="text-sm text-white">{t('createAndList.aiWizard.badge')}</p>
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 hover:text-white cursor-pointer"
                aria-label={t('createAndList.aiWizard.close')}
              >
                <X className="size-5" />
              </button>
            </div>

            <h1 className="text-4xl font-semibold">{t('createAndList.aiWizard.title')}</h1>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'h-1.5 flex-1 rounded-full',
                    index < step ? 'bg-button-primary' : 'bg-white/15',
                  )}
                />
              ))}
            </div>

            {step === 1 && (
              <div className="flex flex-col gap-4">
                <p className="text-lg font-normal">{t('createAndList.aiWizard.step1.question')}</p>
                <div className="flex flex-col gap-1">
                  <Textarea
                    value={formData.pitch}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, pitch: e.target.value }));
                      setServerFieldError(null);
                      touchStep(1);
                    }}
                    placeholder={t('createAndList.aiWizard.step1.placeholder')}
                    maxLength={QUESTION_MAX_LENGTH}
                    className={cn(
                      'min-h-30 resize-none bg-white/5 text-white placeholder:text-tertiary',
                      displayedStepError ? 'border-error' : 'border-white/15',
                    )}
                    autoFocus
                  />
                  {renderCounterRow(formData.pitch, QUESTION_MAX_LENGTH, displayedStepError)}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <p className="text-lg font-normal">{t('createAndList.aiWizard.step2.question')}</p>
                <div className="flex flex-col gap-1">
                  <Textarea
                    value={formData.idealClient}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, idealClient: e.target.value }));
                      setServerFieldError(null);
                      touchStep(2);
                    }}
                    placeholder={t('createAndList.aiWizard.step2.placeholder')}
                    maxLength={QUESTION_MAX_LENGTH}
                    className={cn(
                      'min-h-30 resize-none bg-white/5 text-white placeholder:text-tertiary',
                      displayedStepError ? 'border-error' : 'border-white/15',
                    )}
                    autoFocus
                  />
                  {renderCounterRow(formData.idealClient, QUESTION_MAX_LENGTH, displayedStepError)}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4">
                <p className="text-lg font-normal">{t('createAndList.aiWizard.step3.question')}</p>
                <div className="flex flex-col gap-1">
                  <Textarea
                    value={formData.falseBeliefs}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, falseBeliefs: e.target.value }));
                      setServerFieldError(null);
                      touchStep(3);
                    }}
                    placeholder={t('createAndList.aiWizard.step3.placeholder')}
                    maxLength={QUESTION_MAX_LENGTH}
                    className={cn(
                      'min-h-30 resize-none bg-white/5 text-white placeholder:text-tertiary',
                      displayedStepError ? 'border-error' : 'border-white/15',
                    )}
                    autoFocus
                  />
                  {renderCounterRow(formData.falseBeliefs, QUESTION_MAX_LENGTH, displayedStepError)}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="flex flex-col gap-4">
                <p className="text-lg font-normal">{t('createAndList.aiWizard.step4.question')}</p>
                <div className="flex flex-col gap-1">
                  <Textarea
                    value={formData.uniqueMethod}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, uniqueMethod: e.target.value }));
                      setServerFieldError(null);
                      touchStep(4);
                    }}
                    placeholder={t('createAndList.aiWizard.step4.placeholder')}
                    maxLength={QUESTION_MAX_LENGTH}
                    className={cn(
                      'min-h-30 resize-none bg-white/5 text-white placeholder:text-tertiary',
                      displayedStepError ? 'border-error' : 'border-white/15',
                    )}
                    autoFocus
                  />
                  {renderCounterRow(formData.uniqueMethod, QUESTION_MAX_LENGTH, displayedStepError)}
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="flex flex-col gap-4">
                <p className="text-lg font-normal">{t('createAndList.aiWizard.step5.question')}</p>
                <div className="flex flex-col gap-1">
                  <Textarea
                    value={formData.callToAction}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, callToAction: e.target.value }));
                      setServerFieldError(null);
                      touchStep(5);
                    }}
                    placeholder={t('createAndList.aiWizard.step5.placeholder')}
                    maxLength={QUESTION_MAX_LENGTH}
                    className={cn(
                      'min-h-30 resize-none bg-white/5 text-white placeholder:text-tertiary',
                      displayedStepError ? 'border-error' : 'border-white/15',
                    )}
                    autoFocus
                  />
                  {renderCounterRow(formData.callToAction, QUESTION_MAX_LENGTH, displayedStepError)}
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="flex flex-col gap-2">
                <p className="text-lg font-normal">
                  {t('createAndList.aiWizard.offerLink.question')}{' '}
                  <span className="text-white/50 font-normal">
                    {t('createAndList.aiWizard.offerLink.optional')}
                  </span>
                </p>
                <p className="text-sm text-white/80">
                  {t('createAndList.aiWizard.offerLink.hint')}
                </p>
                <input
                  type="url"
                  value={formData.offerLink}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, offerLink: e.target.value }));
                    setServerFieldError(null);
                    touchStep(6);
                  }}
                  placeholder={t('createAndList.aiWizard.offerLink.placeholder')}
                  maxLength={OFFER_LINK_MAX_LENGTH}
                  className={cn(
                    'mt-2 h-12 rounded-lg border bg-white/5 px-4 text-white placeholder:text-tertiary focus:outline-none',
                    displayedStepError ? 'border-error' : 'border-white/15',
                  )}
                  autoFocus
                />
                {renderCounterRow(formData.offerLink, OFFER_LINK_MAX_LENGTH, displayedStepError)}
              </div>
            )}

            {step === 7 && (
              <div className="flex flex-col gap-2">
                <p className="text-lg font-normal">
                  {t('createAndList.aiWizard.step6.question')}{' '}
                  <span className="text-white/50 font-normal">
                    {t('createAndList.aiWizard.step6.optional')}
                  </span>
                </p>
                <p className="text-sm text-white/80">{t('createAndList.aiWizard.step6.hint')}</p>

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingFile(true);
                  }}
                  onDragLeave={() => setIsDraggingFile(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingFile(false);
                    handleFileSelected(e.dataTransfer.files?.[0]);
                  }}
                  className={cn(
                    'mt-2 rounded-2xl border-2 border-dashed p-8 flex flex-col items-center gap-3 text-center',
                    isDraggingFile ? 'border-button-primary bg-white/5' : 'border-white/20',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="size-10 rounded-full bg-primary flex items-center justify-center cursor-pointer"
                    aria-label={t('createAndList.aiWizard.step6.chooseFile')}
                  >
                    <Plus className="size-5 text-white" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => handleFileSelected(e.target.files?.[0])}
                  />
                  {formData.slidesFile ? (
                    <div className="flex items-center gap-3 w-auto max-w-full rounded-full bg-white/5 border border-white/15 py-2 pl-4 pr-2">
                      <p className="min-w-0 flex-1 truncate text-sm text-white text-left">
                        {formData.slidesFile.name}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile();
                        }}
                        className="flex shrink-0 items-center gap-1.5 rounded-full bg-error/15 hover:bg-error/25 text-error px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors"
                      >
                        <X className="size-3.5" />
                        {t('createAndList.aiWizard.step6.removeFile')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-secondary">
                        {t('createAndList.aiWizard.step6.dropHere')}
                      </p>
                      <p className="text-xs text-white">{t('createAndList.aiWizard.step6.or')}</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-button-primary cursor-pointer"
                      >
                        {t('createAndList.aiWizard.step6.chooseFile')}
                      </button>
                    </>
                  )}
                </div>
                {slideError && (
                  <p className="text-sm text-error">
                    {t(`createAndList.aiWizard.validation.${slideError}`)}
                  </p>
                )}
              </div>
            )}

            {generationError && isLastStep && (
              <p className="text-sm text-error">{generationError}</p>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={handleBack}
                className="border-white/30 h-10 text-white hover:bg-white/10 hover:text-white hover:border-white/50"
              >
                {t('createAndList.aiWizard.back')}
              </Button>
            ) : (
              <span />
            )}
            {isLastStep ? (
              <Button onClick={handleGenerate} className="flex-1 h-10">
                <Sparkles className="size-4" />
                {t('createAndList.aiWizard.generate')}
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={!canGoNext} className="flex-1 max-w-xs">
                {t('createAndList.aiWizard.next')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
